// Number leases (02 §2.2 "Numbers that must be unique, offline").
//
// Tracking numbers and receipt numbers come from ranges the server leased to this device while it
// was online. Offline, the device takes the next number from its ranges. The ranges live in
// IndexedDB (never localStorage), and every number taken is written down BEFORE it is handed out,
// in one IndexedDB transaction, so a crash, a second tab or a restart can never give out the same
// number twice.
import { createStore, get, keys, update } from 'idb-keyval';
import {
  formatTrackingNumber,
  makeReceiptNumber,
  makeTrackingNumber,
  type Permission,
  type TrackingSeriesFormat,
} from '@ecobox/domain';
import type { NumberLease } from '@ecobox/schema';
import type { Connectivity } from './connectivity';
import { createStore as createObservable, type ReadableStore } from './store';

export type NumberSeries = 'tracking' | 'receipt';

/** One leased range on this device. `next` is the next unused sequence; `next > to` means used up. */
export interface StoredRange {
  id: string;
  year: number;
  from: number;
  to: number;
  next: number;
  /**
   * Tracking ranges of a numbering series (03 §3.5): the format the numbers are made with, as the
   * server leased it. Absent: the branch default `{BRANCH}{YY}{SEQ6}{CHECK}`.
   */
  format?: TrackingSeriesFormat;
}

export interface LeaseRequest {
  branchId: string;
  series: NumberSeries;
  deviceId: string;
  count: number;
  /** Tracking numbers of a numbering series other than the branch default (03 §3.5). */
  seriesId?: string;
}

export interface LeaseSummary {
  /** Numbers still unused on this device, by `series:branchId`. */
  counts: Readonly<Record<string, number>>;
  /** A lease request is in flight. */
  requesting: boolean;
  /** Why the last lease request failed, or null. */
  error: string | null;
}

/**
 * Who may hold numbers of a series. The counter needs `cargo.collect` for tracking numbers and
 * `finance.payment.record` for receipt numbers. A driver takes cargo and cash at the door
 * (`completePickup` carries a full `collectCargo`, 04 §4.2) with only `pickup.complete`, so that
 * permission leases both series too. The server's `leaseNumbers` must accept the same (see the
 * Phase 5 notes): without it a driver's phone has no number to give out offline.
 */
export function mayLease(series: NumberSeries, has: (permission: Permission) => boolean): boolean {
  return (
    has('pickup.complete') ||
    has(series === 'tracking' ? 'cargo.collect' : 'finance.payment.record')
  );
}

export const LEASE_REFILL_BELOW = 10;
export const LEASE_SIZE = 50;

const SERIES_WORD: Record<NumberSeries, string> = { tracking: 'tracking', receipt: 'receipt' };

/**
 * Nothing left to hand out, and no way to get more right now. `message` is safe to show.
 * `offline` says why: true when the device has none and no connection to ask for more, false when
 * it asked the server and the request failed (worth a Retry).
 */
export class NoNumbersLeftError extends Error {
  readonly series: NumberSeries;
  readonly offline: boolean;
  constructor(series: NumberSeries, reason?: string) {
    super(
      reason ??
        `No ${SERIES_WORD[series]} numbers left on this device — connect to the internet once`,
    );
    this.name = 'NoNumbersLeftError';
    this.series = series;
    this.offline = reason === undefined;
  }
}

/**
 * What `takeShownNumber` did: it used the number the screen showed, or it used nothing because
 * that number is no longer this device's next one (another tab or another form on this device took
 * it). `next` is then the number the screen should show instead.
 */
export type ShownNumberResult = { taken: true; number: string } | { taken: false; next: string };

export interface LeaseManagerOptions {
  deviceId: string;
  connectivity: Pick<Connectivity, 'getSnapshot' | 'subscribe'>;
  /** Asks the server for a range (the `leaseNumbers` callable). */
  request: (input: LeaseRequest) => Promise<NumberLease>;
  /** IndexedDB database name. Tests use their own so runs do not share ranges. */
  dbName?: string;
  /** Ask for a new range when fewer than this many numbers remain (while online). Default 10. */
  refillBelow?: number;
  /** How many numbers to ask for. Default 50. */
  leaseSize?: number;
  now?: () => Date;
  /**
   * Whether this person may hold numbers of a series at all (tracking numbers need `cargo.collect`,
   * receipt numbers `finance.payment.record`). Background top-ups skip a series they may not
   * lease, so a warehouse phone does not ask the server for numbers it will only be refused.
   */
  allowed?: (series: NumberSeries) => boolean;
}

export interface LeaseManager extends ReadableStore<LeaseSummary> {
  /**
   * The next tracking or receipt number for this branch. Works offline. When the device is
   * online and empty it leases a new range first. Throws `NoNumbersLeftError` when it cannot.
   * `seriesId`: a tracking numbering series other than the branch default (03 §3.5).
   */
  nextNumber(series: NumberSeries, branchId: string, seriesId?: string): Promise<string>;
  /**
   * The number `nextNumber` would hand out now, without using it, so a form can show it from its
   * first step (05 New shipment). Ranges are per device, so nothing else can take it in between
   * except another tab or form on this same device: `takeShownNumber` checks that at save. Leases a
   * range first when the device is online and has none. Throws `NoNumbersLeftError` when it cannot.
   */
  peekNumber(series: NumberSeries, branchId: string, seriesId?: string): Promise<string>;
  /**
   * Uses exactly the number the screen showed (from `peekNumber`). If it is no longer the next one,
   * nothing is used and the result says which number is next now, so the screen can show it and
   * tell the person before anything is saved. Throws `NoNumbersLeftError` like `nextNumber`.
   */
  takeShownNumber(
    series: NumberSeries,
    branchId: string,
    shown: string,
    seriesId?: string,
  ): Promise<ShownNumberResult>;
  /** Numbers left on this device (reads IndexedDB). */
  remaining(series: NumberSeries, branchId: string, seriesId?: string): Promise<number>;
  /**
   * Top up both series for a branch if they are low and the device is online, and every other
   * tracking numbering series this device has used at the branch, so each keeps working offline.
   * Never throws.
   */
  ensure(branchId: string): Promise<void>;
  /** Top up whenever the branch changes or the device comes back online. Returns a stop function. */
  watch(branch: ReadableStore<{ current: string | null }>): () => void;
}

/**
 * The IndexedDB key of a branch's ranges of a series. The branch default numbering series has the
 * branch code as its id and keeps the key it always had (`tracking:DXB`); another numbering series
 * used by the branch's devices is `tracking:DXB:nsr_…`.
 */
const keyOf = (series: NumberSeries, branchId: string, seriesId?: string): string =>
  series === 'tracking' && seriesId !== undefined && seriesId !== branchId
    ? `${series}:${branchId}:${seriesId}`
    : `${series}:${branchId}`;

/** Where a branch's numbers of one series (and numbering series) live. */
interface Slot {
  series: NumberSeries;
  branchId: string;
  /** A tracking numbering series other than the branch default. */
  seriesId?: string;
  key: string;
}

const slotOf = (series: NumberSeries, branchId: string, seriesId?: string): Slot => ({
  series,
  branchId,
  ...(series === 'tracking' && seriesId !== undefined && seriesId !== branchId ? { seriesId } : {}),
  key: keyOf(series, branchId, seriesId),
});

/** Ranges still giving numbers: of this year, or of a numbering series without a year part. */
function usable(ranges: readonly StoredRange[] | undefined, year: number): StoredRange[] {
  return (ranges ?? []).filter(
    (r) => (r.year === year || (r.format !== undefined && !r.format.yearPart)) && r.next <= r.to,
  );
}

function countOf(ranges: readonly StoredRange[]): number {
  return ranges.reduce((n, r) => n + (r.to - r.next + 1), 0);
}

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function createLeaseManager(options: LeaseManagerOptions): LeaseManager {
  const {
    deviceId,
    connectivity,
    request,
    refillBelow = LEASE_REFILL_BELOW,
    leaseSize = LEASE_SIZE,
    now = () => new Date(),
    allowed = () => true,
  } = options;
  const kv = createStore(options.dbName ?? 'ecobox-leases', 'ranges');
  const summary = createObservable<LeaseSummary>({ counts: {}, requesting: false, error: null });
  const inFlight = new Map<string, Promise<void>>();
  /** Serialises the calls that read or take a number (see `withNumbers`). */
  let queue: Promise<unknown> = Promise.resolve();

  const year = (): number => now().getFullYear();
  const online = (): boolean => connectivity.getSnapshot().online;

  const setCount = (key: string, count: number): void => {
    const s = summary.getSnapshot();
    if (s.counts[key] === count) return;
    summary.set({ ...s, counts: { ...s.counts, [key]: count } });
  };

  const readCount = async (slot: Slot): Promise<number> => {
    const ranges = await get<StoredRange[]>(slot.key, kv);
    const n = countOf(usable(ranges, year()));
    setCount(slot.key, n);
    return n;
  };

  const format = (slot: Slot, range: StoredRange, seq: number): string =>
    slot.series === 'receipt'
      ? makeReceiptNumber({ branch: slot.branchId, year: range.year, seq })
      : range.format
        ? formatTrackingNumber(range.format, seq, range.year)
        : makeTrackingNumber({ branch: slot.branchId, year: range.year, seq });

  /** Take the next number in a single IndexedDB transaction. */
  const take = async (slot: Slot): Promise<string | undefined> => {
    const y = year();
    let taken: string | undefined;
    await update<StoredRange[]>(
      slot.key,
      (old) => {
        const list = usable(old, y);
        const first = list[0];
        if (first) {
          taken = format(slot, first, first.next);
          first.next += 1;
        }
        return list.filter((r) => r.next <= r.to);
      },
      kv,
    );
    return taken;
  };

  /** The next number, read without taking it. */
  const look = async (slot: Slot): Promise<string | undefined> => {
    const first = usable(await get<StoredRange[]>(slot.key, kv), year())[0];
    return first ? format(slot, first, first.next) : undefined;
  };

  /**
   * Take the next number only if it is `shown`, in a single IndexedDB transaction. Answers what it
   * found: the number it took, or the number that is next instead.
   */
  const takeIf = async (slot: Slot, shown: string): Promise<ShownNumberResult | undefined> => {
    const y = year();
    let found: ShownNumberResult | undefined;
    await update<StoredRange[]>(
      slot.key,
      (old) => {
        const list = usable(old, y);
        const first = list[0];
        if (first) {
          const number = format(slot, first, first.next);
          if (number === shown) {
            found = { taken: true, number };
            first.next += 1;
          } else {
            found = { taken: false, next: number };
          }
        }
        return list.filter((r) => r.next <= r.to);
      },
      kv,
    );
    return found;
  };

  const lease = (slot: Slot): Promise<void> => {
    const running = inFlight.get(slot.key);
    if (running) return running;
    const job = (async () => {
      summary.set({ ...summary.getSnapshot(), requesting: true });
      try {
        const got = await request({
          branchId: slot.branchId,
          series: slot.series,
          deviceId,
          count: leaseSize,
          ...(slot.seriesId ? { seriesId: slot.seriesId } : {}),
        });
        await update<StoredRange[]>(
          slot.key,
          (old) => {
            const list = old ?? [];
            if (list.some((r) => r.id === got.id)) return list;
            return [
              ...list,
              {
                id: got.id,
                year: got.year,
                from: got.from,
                to: got.to,
                next: got.from,
                ...(got.format ? { format: got.format } : {}),
              },
            ];
          },
          kv,
        );
        summary.set({ ...summary.getSnapshot(), error: null });
      } catch (err) {
        summary.set({ ...summary.getSnapshot(), error: messageOf(err) });
        throw err;
      } finally {
        inFlight.delete(slot.key);
        summary.set({ ...summary.getSnapshot(), requesting: false });
        await readCount(slot);
      }
    })();
    inFlight.set(slot.key, job);
    return job;
  };

  const refillInBackground = (slot: Slot, left: number): void => {
    if (left >= refillBelow || !online()) return;
    lease(slot).catch((err: unknown) => {
      console.warn(`Could not lease more ${slot.series} numbers.`, err);
    });
  };

  /**
   * Runs `attempt` against this device's ranges. When it finds nothing and the device is online it
   * leases a range and tries once more. Calls are queued one after the other, so two quick scans
   * (or a peek and a save) never wait on each other's lease request twice or see a half-taken range.
   */
  const withNumbers = <T>(slot: Slot, attempt: () => Promise<T | undefined>): Promise<T> => {
    const run = async (): Promise<T> => {
      let got = await attempt();
      if (got === undefined) {
        if (!online()) throw new NoNumbersLeftError(slot.series);
        try {
          await lease(slot);
        } catch (err) {
          throw new NoNumbersLeftError(
            slot.series,
            `Could not get new ${SERIES_WORD[slot.series]} numbers from the server (${messageOf(err)}). Try again in a moment.`,
          );
        }
        got = await attempt();
        if (got === undefined) throw new NoNumbersLeftError(slot.series);
      }
      const left = await readCount(slot);
      refillInBackground(slot, left);
      return got;
    };
    const result = queue.then(run, run);
    queue = result.catch(() => undefined);
    return result;
  };

  const ensureSlot = async (slot: Slot): Promise<void> => {
    try {
      const left = await readCount(slot);
      if (left < refillBelow && online()) await lease(slot);
    } catch (err) {
      console.warn(`Could not top up ${slot.series} numbers for ${slot.branchId}.`, err);
    }
  };

  const ensure = async (branchId: string): Promise<void> => {
    for (const series of ['tracking', 'receipt'] as const) {
      if (!allowed(series)) continue;
      await ensureSlot(slotOf(series, branchId));
    }
    // The other numbering series this device has used here (a customer's, an agent's).
    if (!allowed('tracking')) return;
    let stored: IDBValidKey[];
    try {
      stored = await keys(kv);
    } catch {
      return;
    }
    const prefix = `tracking:${branchId}:`;
    for (const key of stored) {
      if (typeof key === 'string' && key.startsWith(prefix)) {
        await ensureSlot(slotOf('tracking', branchId, key.slice(prefix.length)));
      }
    }
  };

  return {
    subscribe: summary.subscribe,
    getSnapshot: summary.getSnapshot,
    nextNumber: (series, branchId, seriesId) => {
      const slot = slotOf(series, branchId, seriesId);
      return withNumbers(slot, () => take(slot));
    },
    peekNumber: (series, branchId, seriesId) => {
      const slot = slotOf(series, branchId, seriesId);
      return withNumbers(slot, () => look(slot));
    },
    takeShownNumber: (series, branchId, shown, seriesId) => {
      const slot = slotOf(series, branchId, seriesId);
      return withNumbers(slot, () => takeIf(slot, shown));
    },
    remaining: (series, branchId, seriesId) => readCount(slotOf(series, branchId, seriesId)),
    ensure,
    watch(branch) {
      let last = '';
      const check = (): void => {
        const current = branch.getSnapshot().current;
        if (current && online()) {
          // Nothing to top up (yet): try again when the branch or the connection changes.
          if (!allowed('tracking') && !allowed('receipt')) return;
          const stamp = `${current}|${connectivity.getSnapshot().since}`;
          if (stamp === last) return;
          last = stamp;
          void ensure(current);
        } else if (current) {
          void readCount(slotOf('tracking', current));
          void readCount(slotOf('receipt', current));
        }
      };
      const stops = [branch.subscribe(check), connectivity.subscribe(check)];
      check();
      return () => {
        for (const stop of stops) stop();
      };
    },
  };
}

/** Numbers left for a series at a branch, from the summary (0 until it has been read). */
export function leaseCount(
  summary: LeaseSummary,
  series: NumberSeries,
  branchId: string,
  seriesId?: string,
): number {
  return summary.counts[keyOf(series, branchId, seriesId)] ?? 0;
}
